import { useState } from "react"

import { PencilSquareIcon } from "@heroicons/react/20/solid"
import { useNavigate } from "react-router-dom"

import type { SolarDesignVersionsResultDto } from "~/api/dto/solar-design-versions.dto"
import type { SolarAuroraWarning } from "~/api/dto/solar-proposal-aurora.dto"
import type { SolarProposalDto } from "~/api/dto/solar-proposal.dto"
import useSolarProposalAurora from "~/api/hooks/useSolarProposalAurora"
import useSolarProposalDesign from "~/api/hooks/useSolarProposalDesign"

import { SolarAuroraDesignDrawer } from "./aurora/SolarAuroraDesignDrawer"
import { SolarAuroraLink } from "./aurora/SolarAuroraLink"
import { designerNoticesState } from "./designer/designer-notices-state"
import { toSolarSystem } from "./solar-proposal-design"
import { solarDesignerPath } from "./solar-setup-paths"
import { SolarDesignChangeOutcome } from "./SolarDesignChangeOutcome"
import {
  DUPLICATE_PRODUCTION_WARNING,
  SolarDesignVersions,
} from "./SolarDesignVersions"

const primaryButtonClass = `inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50`

/**
 * The System Design step. A Vision design shows its summary, what a design
 * change left out of date, a production retry after a save that couldn't update
 * it, Open designer (for editors) and the design versions. An Aurora design is
 * edited in Aurora, so the step is the Aurora drawer instead, with any Vision
 * versions listed read-only. Before any Aurora design, an administrator the
 * Aurora read allows sees Link Aurora design; a financing lock hides it.
 */
export const SolarDesignStep = ({
  proposal,
}: {
  proposal: SolarProposalDto
}): JSX.Element => {
  const navigate = useNavigate()
  const query = useSolarProposalDesign(proposal.id)
  // Stored state only; the drawer shares this read.
  const aurora = useSolarProposalAurora(proposal.id)
  // Set by a first link made here, until the step unmounts.
  const [linkWarnings, setLinkWarnings] = useState<SolarAuroraWarning[] | null>(
    null,
  )
  const linkAurora =
    aurora.data?.status === `aurora_not_linked` &&
    aurora.data.viewerCanLinkAurora &&
    !aurora.data.financingLocked ? (
      <SolarAuroraLink
        proposalId={proposal.id}
        onLinked={(saved) => setLinkWarnings(saved.warnings)}
      />
    ) : null
  const openDesigner = (): void => navigate(solarDesignerPath(proposal.id))
  // The designer says so when the copy's production couldn't be updated.
  const openDuplicate = (result: SolarDesignVersionsResultDto): void =>
    navigate(solarDesignerPath(proposal.id), {
      state: result.warnings.includes(`production_not_updated`)
        ? designerNoticesState([DUPLICATE_PRODUCTION_WARNING])
        : undefined,
    })

  if (!query.data) {
    return query.isError ? (
      <p role="alert" className="text-sm text-danger">
        Unable to load the system design
      </p>
    ) : (
      <div
        role="status"
        aria-label="Loading the system design"
        className="h-40 animate-pulse rounded-xl bg-muted"
      />
    )
  }

  const { design } = query.data
  if (!design) {
    // A version that lost its activation can still be switched to here.
    return (
      <div className="flex flex-col gap-6">
        <p role="status" className="text-sm text-muted">
          This proposal has no active design yet.
        </p>
        {linkAurora}
        <SolarDesignVersions
          proposalId={proposal.id}
          viewerCanUpdate={proposal.viewerCanUpdate}
          onDuplicated={openDuplicate}
          hideWhenEmpty
        />
      </div>
    )
  }

  if (design.source === `aurora`) {
    return (
      <div className="flex flex-col gap-6">
        <SolarAuroraDesignDrawer
          proposalId={proposal.id}
          viewerCanUpdate={proposal.viewerCanUpdate}
          linkWarnings={linkWarnings}
        />
        <SolarDesignVersions
          proposalId={proposal.id}
          viewerCanUpdate={proposal.viewerCanUpdate}
          onDuplicated={openDuplicate}
          hideWhenEmpty
        />
      </div>
    )
  }

  const { specs } = toSolarSystem(design)

  return (
    <div className="flex flex-col gap-6" data-testid="solar-design-step">
      <SolarDesignChangeOutcome
        proposalId={proposal.id}
        productionNotUpdated={query.data.productionStatus === `not_updated`}
        testId="solar-design-out-of-date"
      />
      <dl className="grid grid-cols-2 gap-3">
        {specs.map((spec) => (
          <div
            key={spec.label}
            className="flex flex-col gap-1 rounded-lg border border-border bg-surface p-4"
          >
            <dt className="text-xs font-semibold tracking-wide text-muted uppercase">
              {spec.label}
            </dt>
            <dd className="text-lg font-semibold text-foreground tabular-nums">
              {spec.value}
            </dd>
          </div>
        ))}
      </dl>
      {proposal.viewerCanUpdate ? (
        <button
          type="button"
          className={primaryButtonClass}
          onClick={openDesigner}
        >
          <PencilSquareIcon aria-hidden="true" className="size-4" />
          Open designer
        </button>
      ) : null}
      {linkAurora}
      <SolarDesignVersions
        proposalId={proposal.id}
        viewerCanUpdate={proposal.viewerCanUpdate}
        onDuplicated={openDuplicate}
      />
    </div>
  )
}
